import React, { useCallback, useMemo } from 'react';
import {
  GetPractitionerSearchResultsDocument,
  HistoryAction,
  HistoryType,
  PractitionerSearchEdge,
  SearchSortType,
} from '__generated__/graphql';
import { PractitionerResult } from 'pages/practitioner/PractitionerResult';
import { TypedSearchResults } from '../TypedSearchResults';
import { EmptyResults } from '../EmptyResults';
import styled from 'styled-components';
import TitledPage from 'components/common/TitledPage';
import { SearchFilters, StyledSearchWithin } from '../SearchFilters';
import { PractitionerFilters } from './PractitionerFilters';

import { PractitionerSearchDoctypeTabs } from './PractitionerSearchDoctypeTabs';
import { QueryInput } from 'components/common/query-input';
import { useAssetAccess } from 'components/asset-access';
import { usePractitionerOptionFilters } from './usePractitionerOptionFilters';
import { FilterChips } from '../FilterChips';
import { usePractitionerSearchCounts } from './usePractitionerSearchCounts';
import {
  StyledFormCheckbox,
  StyledSearchCheckboxes,
  StyledSearchResults,
  StyledSearchHeader,
  StyledSearchContent,
} from '../SearchStyles';
import useSearchVariables from '../useSearchVariables';
import { SearchWithin } from '../SearchWithin';
import { TrackHistory } from 'hooks/useHistory';

export function PractitionerSearch() {
  const { hasPractitionerAllAccess } = useAssetAccess();

  const outofplanOptionFilters = usePractitionerOptionFilters();

  const mutateVariables: NonNullable<Parameters<typeof useSearchVariables>[0]> =
    useCallback(
      (variables, queryString) => {
        return {
          ...variables,
          optionFilters: queryString['doctype']
            ? [
                queryString['doctype'],
                ...variables.optionFilters,
                ...outofplanOptionFilters,
              ]
            : [...variables.optionFilters, ...outofplanOptionFilters],
          sort: (queryString.sort as SearchSortType) || undefined,
        };
      },
      [outofplanOptionFilters],
    );

  const variables = useSearchVariables(mutateVariables);

  const counts = usePractitionerSearchCounts(variables);

  const totalCount = useMemo(
    () =>
      counts
        ? Object.values(counts).reduce((sum, count) => sum + count, 0)
        : null,
    [counts],
  );

  const hasTabs = useMemo(
    () => (totalCount !== null ? totalCount > 0 : true),
    [totalCount],
  );

  return (
    <TitledPage title="Search Practitioner">
      <TrackHistory
        text={variables.query}
        type={HistoryType.Practitioner}
        action={HistoryAction.Search}
        subtype="SEARCH_PRACTITIONER"
      />

      <SearchFilters queryVariables={['textFilters', 'optionFilters']}>
        <PractitionerFilters />
      </SearchFilters>

      <StyledSearchContent>
        <StyledSearchHeader>
          <div>
            <StyledSearchWithin>{<SearchWithin />}</StyledSearchWithin>

            <StyledSearchCheckboxes>
              <QueryInput
                through={StyledFormCheckbox}
                type="checkbox"
                name="sort"
                placeholder="Sort by date"
                value="UPDATED_AT"
                className="date-sort"
                mutate={() => ({ page: undefined })}
              />
              {!hasPractitionerAllAccess && (
                <QueryInput
                  through={StyledFormCheckbox}
                  type="checkbox"
                  name="outofplan"
                  value="yes"
                  placeholder="Show out-of-plan content"
                />
              )}
            </StyledSearchCheckboxes>
          </div>
          <div>
            <FilterChips
              filters={{
                'Practice Area': 'practicearea_',
                'Task Type': 'legaltask_',
                Courts: 'court_',
              }}
            />
          </div>
        </StyledSearchHeader>
        {hasTabs && <PractitionerSearchDoctypeTabs counts={counts} />}
        <StyledSearchResults hasHeader>
          <TypedSearchResults
            resultTitle="Practitioner Result"
            query={GetPractitionerSearchResultsDocument}
            emptyResults={<EmptyResults />}
            variables={variables}
          >
            {result => (
              <StyledPractitionerResult
                key={(result as PractitionerSearchEdge).node.id}
                result={result as PractitionerSearchEdge}
                noIcons
              />
            )}
          </TypedSearchResults>
        </StyledSearchResults>
      </StyledSearchContent>
    </TitledPage>
  );
}

const StyledPractitionerResult = styled(PractitionerResult)`
  padding-left: 0;
`;

StyledPractitionerResult.displayName = 'StyledPractitionerResult';
